import React, { useState, useEffect, useCallback } from 'react';
import { api, formatApiError } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { DownloadSimple, Receipt, ShoppingCart, Trash, MagicWand, Upload, Eye } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';

const InvoiceRow = ({ inv, onScan, onDelete, onDownload, onPreview }) => (
  <div className="flex items-center justify-between px-4 py-3" data-testid={`invoice-row-${inv.id}`}>
    <div className="flex-1 min-w-0">
      <p className="font-medium text-sm truncate">{inv.original_filename || inv.invoice_number || inv.customer_name || 'Invoice'}</p>
      <div className="flex items-center gap-2 mt-0.5 flex-wrap">
        {inv.created_at && <span className="text-xs text-muted-foreground">{new Date(inv.created_at).toLocaleDateString()}</span>}
        {inv.uploaded_by === 'client' && <Badge variant="secondary" className="text-[10px] py-0 bg-blue-50 text-blue-600">Client Upload</Badge>}
        {inv.status && <Badge className={`text-[10px] py-0 ${inv.status === 'matched' ? 'bg-green-50 text-green-600' : 'bg-amber-50 text-amber-600'}`}>{inv.status}</Badge>}
        {inv.supplier_name && <span className="text-xs text-muted-foreground">{inv.supplier_name}</span>}
        {inv.amount > 0 && <span className="text-xs font-medium">£{Number(inv.amount).toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span>}
        {inv.total > 0 && !inv.amount && <span className="text-xs font-medium">£{Number(inv.total).toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span>}
      </div>
    </div>
    <div className="flex items-center gap-1">
      {inv.file_path && !inv.scanned && inv._type !== 'client_sales' && (
        <Button variant="outline" size="sm" onClick={() => onScan(inv.id)} data-testid={`scan-${inv.id}`}><MagicWand className="w-3.5 h-3.5 mr-1" /> Scan</Button>
      )}
      {inv.file_path && (
        <Button variant="outline" size="sm" onClick={() => onPreview(inv)} data-testid={`preview-${inv.id}`}><Eye className="w-3.5 h-3.5 mr-1" /> Preview</Button>
      )}
      {inv.file_path && (
        <Button variant="outline" size="sm" onClick={() => onDownload(inv)} data-testid={`download-${inv.id}`}><DownloadSimple className="w-3.5 h-3.5 mr-1" /> Download</Button>
      )}
      {inv._type !== 'client_sales' && (
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onDelete(inv.id)}><Trash className="w-4 h-4 text-destructive" /></Button>
      )}
    </div>
  </div>
);

export const ClientInvoicesTab = ({ clientId }) => {
  const [invoices, setInvoices] = useState([]);
  const [salesInvoices, setSalesInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingPurchase, setUploadingPurchase] = useState(false);
  const [uploadingSales, setUploadingSales] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewName, setPreviewName] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const { data } = await api.get(`/clients/${clientId}/all-uploads`);
      setInvoices(data.invoices || []);
      setSalesInvoices(data.sales_invoices || []);
    } catch (error) { toast.error(formatApiError(error)); }
    finally { setLoading(false); }
  }, [clientId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const purchaseInvoices = invoices.filter(i => i.invoice_type === 'purchase').sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
  const adminSalesInvoices = invoices.filter(i => i.invoice_type === 'sales').sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
  const clientSales = salesInvoices.map(si => ({ ...si, _type: 'client_sales', uploaded_by: 'client' })).sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
  const allSales = [...adminSalesInvoices, ...clientSales];

  const handleUpload = async (e, invoiceType) => {
    const files = e.target.files; if (!files?.length) return;
    const setter = invoiceType === 'purchase' ? setUploadingPurchase : setUploadingSales;
    setter(true);
    const formData = new FormData();
    for (const file of files) formData.append('files', file);
    try {
      await api.post(`/invoices/upload-multi?client_id=${clientId}&invoice_type=${invoiceType}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`${files.length} ${invoiceType} invoice(s) uploaded & scanning`);
      fetchData();
    } catch (error) { toast.error(formatApiError(error)); }
    finally { setter(false); e.target.value = ''; }
  };

  const handleScan = async (invoiceId) => {
    try { toast.info('Scanning...'); await api.post(`/invoices/${invoiceId}/scan`); toast.success('Scanned!'); fetchData(); }
    catch (error) { toast.error(formatApiError(error)); }
  };

  const handleDelete = async (invoiceId) => {
    if (!window.confirm('Delete this invoice?')) return;
    try { await api.delete(`/invoices/${invoiceId}`); toast.success('Deleted'); fetchData(); }
    catch (error) { toast.error(formatApiError(error)); }
  };

  const handleDownload = async (inv) => {
    try {
      const response = await api.get(`/files/${inv.file_path}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a'); a.href = url; a.download = inv.original_filename || 'invoice';
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch { toast.error('Failed to download'); }
  };

  const handlePreview = async (inv) => {
    try {
      const response = await api.get(`/files/${inv.file_path}`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      setPreviewUrl(url);
      setPreviewName(inv.original_filename || inv.invoice_number || 'Invoice');
    } catch { toast.error('Failed to load preview'); }
  };

  if (loading) return <div className="flex items-center justify-center h-32"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="client-invoices-tab">
      {/* Purchase Invoices */}
      <Card className="border border-border">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="heading-4 flex items-center gap-2"><Receipt className="w-5 h-5" /> Purchase Invoices <Badge variant="outline" className="ml-2">{purchaseInvoices.length}</Badge></CardTitle>
              <CardDescription>Invoices from suppliers — uploaded by admin or client</CardDescription>
            </div>
            <div>
              <input type="file" id="upload-purchase" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" multiple onChange={(e) => handleUpload(e, 'purchase')} className="hidden" data-testid="upload-purchase-input" />
              <Button variant="outline" onClick={() => document.getElementById('upload-purchase').click()} disabled={uploadingPurchase} data-testid="upload-purchase-btn">
                <Upload className="w-4 h-4 mr-2" /> {uploadingPurchase ? 'Uploading...' : 'Upload Purchase Invoice'}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {purchaseInvoices.length === 0 ? (
            <div className="text-center py-6"><p className="text-sm text-muted-foreground">No purchase invoices yet</p></div>
          ) : (
            <div className="divide-y">{purchaseInvoices.map((inv) => <InvoiceRow key={inv.id} inv={inv} onScan={handleScan} onDelete={handleDelete} onDownload={handleDownload} onPreview={handlePreview} />)}</div>
          )}
        </CardContent>
      </Card>

      {/* Sales Invoices */}
      <Card className="border border-border">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="heading-4 flex items-center gap-2"><ShoppingCart className="w-5 h-5" /> Sales Invoices <Badge variant="outline" className="ml-2">{allSales.length}</Badge></CardTitle>
              <CardDescription>Client-created sales invoices and admin uploads</CardDescription>
            </div>
            <div>
              <input type="file" id="upload-sales" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" multiple onChange={(e) => handleUpload(e, 'sales')} className="hidden" data-testid="upload-sales-input" />
              <Button variant="outline" onClick={() => document.getElementById('upload-sales').click()} disabled={uploadingSales} data-testid="upload-sales-btn">
                <Upload className="w-4 h-4 mr-2" /> {uploadingSales ? 'Uploading...' : 'Upload Sales Invoice'}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {allSales.length === 0 ? (
            <div className="text-center py-6"><p className="text-sm text-muted-foreground">No sales invoices yet</p></div>
          ) : (
            <div className="divide-y">{allSales.map((inv) => <InvoiceRow key={inv.id} inv={inv} onScan={handleScan} onDelete={handleDelete} onDownload={handleDownload} onPreview={handlePreview} />)}</div>
          )}
        </CardContent>
      </Card>

      {/* Preview Dialog */}
      <Dialog open={!!previewUrl} onOpenChange={(open) => { if (!open) { if (previewUrl) window.URL.revokeObjectURL(previewUrl); setPreviewUrl(null); } }}>
        <DialogContent className="max-w-4xl max-h-[92vh] p-0">
          <DialogHeader className="px-6 pt-6 pb-2">
            <DialogTitle data-testid="invoice-preview-title">{previewName}</DialogTitle>
          </DialogHeader>
          <div className="px-6 pb-6">
            {previewUrl && (
              <iframe
                src={previewUrl}
                className="w-full rounded-md border"
                style={{ height: '70vh' }}
                title="Invoice Preview"
                data-testid="invoice-preview-iframe"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
